import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Typography,
  Stack,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  IconButton,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import * as Yup from 'yup';
import { useThemeColors } from '../../hooks/useThemeColors';
import {
  FormInput,
  FormSection,
} from '../Stateless/FormFields';
import { useField, useFormikContext } from 'formik';
import { resizeImage } from './Utils/resizeImage';
import { uploadImageWithPresignedUrl } from '../HelperFunctions/s3Service';

import {
  CreateRestaurantCategoryRequest,
  UpdateRestaurantCategoryRequest,
} from '../../client-sdk';
import {
  useAddCategory,
  useEditStoreCategory,
} from '../Query/AddStoreCategoryQuery';
import CloseIcon from '@mui/icons-material/Close';
import { useContext } from 'react';
import { HimmaContext } from '../../context/HimmaContext';

// Custom ImageUploadField that matches FormFileUpload UI
interface CustomImageUploadFieldProps {
  name: string;
  label: string;
  required?: boolean;
  sizeRequirements?: string;
}

const CustomImageUploadField: React.FC<CustomImageUploadFieldProps> = ({
  name,
  label,
  required = false,
}) => {
  const [field, meta] = useField(name);
  const form = useFormikContext<any>();
  const { error: errorColors } = useThemeColors();
  const [isLoading, setIsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [imageBlobUrls, setImageBlobUrls] = useState<string[]>([]);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const apiClient = useContext(HimmaContext);
  

  // useEffect(() => {
  //   if (field.value) {
  //     setPreviewUrl(field.value);
  //   } else {
  //     setPreviewUrl(null);
  //   }
  // }, [field.value]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    form.setFieldTouched(name, true);

    if (files.length === 0) {
      form.setFieldError(name, `Please upload ${label.toLowerCase()}`);
      return;
    }

    try {
      setIsLoading(true);
      if (!apiClient) {
        throw new Error('API client not available');
      }
      const resizedFiles = await Promise.all(files.map(file => resizeImage(file, label)));
      const uploaded = await Promise.all(resizedFiles.map(file => uploadImageWithPresignedUrl(file, apiClient)));
      const urls = uploaded.map(res => res.url);

      form.setFieldValue(name, urls[0]);
      form.setFieldError(name, undefined);
    } catch (err) {
      console.error(err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : `Error uploading ${label.toLowerCase()}`;
      form.setFieldError(name, errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    form.setFieldValue(name, '');
    form.setFieldError(name, undefined);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const labelStyle = {
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '0.875rem',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '20px',
    height: '100%',
    textAlign: 'right' as const,
  };

  const fileStatusText = isLoading
    ? 'Uploading...'
    : field.value
      ? 'File uploaded'
      : 'No file chosen';

  const uploadedImages = field.value ? [field.value] : [];

  // Function to get clean URL for preview
  const getCleanUrlForPreview = (imageUrl: string) => {
    // For preview, we should use the clean URL without query parameters
    // If the URL contains query parameters (like presigned URLs), remove them
    const cleanUrl = imageUrl.split('?')[0];
    console.log('Using clean URL for preview:', cleanUrl);
    return cleanUrl;
  };

  // Function to load images when preview opens
  const loadImagesForPreview = async () => {
    if (!field.value) return;
    
    try {
      setIsLoadingImage(true);
      const cleanUrl = getCleanUrlForPreview(field.value);
      setImageBlobUrls([cleanUrl]);
    } catch (error) {
      console.error('Error loading images for preview:', error);
      setImageBlobUrls([field.value]); // Fallback to original URL
    } finally {
      setIsLoadingImage(false);
    }
  };

  // Load images when preview dialog opens
  useEffect(() => {
    if (previewOpen) {
      console.log('Preview dialog opened, loading images...');
      loadImagesForPreview();
    } else {
      // Clean up blob URLs when dialog closes
      imageBlobUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      setImageBlobUrls([]);
    }
  }, [previewOpen]);

  return (
    <>
      <Box sx={{ display: 'flex', mb: 2, minHeight: '40px' }}>
        <Box sx={{ flex: '0 0 200px' }}>
          <Typography sx={labelStyle}>
            {label}
            {required && '*'}:
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormControl error={Boolean(meta.touched && meta.error)}>
            <input
              ref={inputRef}
              type="file"
              onChange={handleChange}
              style={{ display: 'none' }}
              id={`${name}-upload`}
              accept="image/*"
              disabled={isLoading}
            />
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <label htmlFor={`${name}-upload`}>
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={isLoading}
                    sx={{
                      bgcolor: meta.touched && meta.error ? errorColors.light : '#f5f5f5',
                      color: meta.touched && meta.error ? errorColors.main : '#000',
                      textTransform: 'none',
                      height: '35px',
                      borderColor: meta.touched && meta.error ? errorColors.main : 'black',
                      '&:hover': {
                        bgcolor: meta.touched && meta.error ? errorColors.light : '#e0e0e0',
                        borderColor: meta.touched && meta.error ? errorColors.main : 'black',
                      },
                    }}
                  >
                    {isLoading ? 'Uploading...' : 'Choose File'}
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary">
                  {fileStatusText}
                </Typography>
                {field.value && !isLoading && field.value.trim() !== '' && (
                  <>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleClear}
                      sx={{
                        height: '30px',
                        textTransform: 'uppercase',
                        bgcolor: errorColors.main,
                        '&:hover': {
                          bgcolor: errorColors.dark,
                        },
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setPreviewOpen(true)}
                      sx={{ height: '30px', textTransform: 'uppercase' }}
                    >
                      Preview
                    </Button>
                  </>
                )}
                {isLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Uploading...</Typography>
                  </Box>
                )}
              </Box>
              {/* {previewUrl && (
                <Box sx={{ mt: 1 }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              )} */}
              <Box>
                {meta.touched && meta.error && (
                  <FormHelperText
                    error
                    sx={{ color: errorColors.main, margin: 0 }}
                  >
                    {meta.error}
                  </FormHelperText>
                )}
              </Box>
            </Stack>
          </FormControl>
        </Box>
      </Box>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Preview {label}
          <IconButton
            aria-label="close"
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabIndex} onChange={(_, newVal) => setTabIndex(newVal)} sx={{ mb: 2 }}>
            <Tab label="Mobile Image" />
            <Tab label="Web Image" />
          </Tabs>

          {uploadedImages.length > 0 ? (
            <>
              {isLoadingImage ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading image with PUT method...
                  </Typography>
                </Box>
              ) : imageBlobUrls.length > 0 ? imageBlobUrls.map((url: string, idx: number) => (
                <Box key={idx} sx={{ display: tabIndex === 0 ? 'block' : 'none' }}>
                  {/* Mobile Preview */}
                  <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
                    <img
                      src={url}
                      alt={`Mobile Preview ${idx + 1}`}
                      style={{
                        width: '100%',
                        maxWidth: '300px',
                        height: 'auto',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                      }}
                      onError={(e) => {
                        console.error('Image load error:', url);
                        const errorDiv = document.createElement('div');
                        errorDiv.innerHTML = `
                          <div style="
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            padding: 20px;
                            border: 2px dashed #ccc;
                            border-radius: 8px;
                            background-color: #f9f9f9;
                            color: #666;
                            text-align: center;
                          ">
                            <div style="font-size: 48px; margin-bottom: 10px;">🖼️</div>
                            <div>Failed to load image</div>
                            <div style="font-size: 12px; margin-top: 5px; word-break: break-all;">${url}</div>
                          </div>
                        `;
                        e.currentTarget.parentNode?.replaceChild(errorDiv, e.currentTarget);
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', url);
                        console.log('Image loaded using blob URL (fetched with PUT method)');
                      }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      Mobile Preview {idx + 1}
                    </Typography>
                  </Box>
                </Box>
              )) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No image loaded
                  </Typography>
                </Box>
              )}

              {isLoadingImage ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress sx={{ mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Loading image with PUT method...
                  </Typography>
                </Box>
              ) : imageBlobUrls.length > 0 ? imageBlobUrls.map((url: string, idx: number) => (
                <Box key={idx} sx={{ display: tabIndex === 1 ? 'block' : 'none' }}>
                  {/* Web Preview */}
                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <img
                      src={url}
                      alt={`Web Preview ${idx + 1}`}
                      style={{
                        width: '100%',
                        maxWidth: '600px',
                        height: 'auto',
                        objectFit: 'cover',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                      }}
                      onError={(e) => {
                        console.error('Image load error:', url);
                        const errorDiv = document.createElement('div');
                        errorDiv.innerHTML = `
                          <div style="
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            padding: 20px;
                            border: 2px dashed #ccc;
                            border-radius: 8px;
                            background-color: #f9f9f9;
                            color: #666;
                            text-align: center;
                          ">
                            <div style="font-size: 48px; margin-bottom: 10px;">🖼️</div>
                            <div>Failed to load image</div>
                            <div style="font-size: 12px; margin-top: 5px; word-break: break-all;">${url}</div>
                          </div>
                        `;
                        e.currentTarget.parentNode?.replaceChild(errorDiv, e.currentTarget);
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', url);
                        console.log('Web preview loaded using blob URL (fetched with PUT method)');
                      }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      Web Preview {idx + 1}
                    </Typography>
                  </Box>
                </Box>
              )) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No image loaded
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No image to preview
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const CategoryForm = ({
  mode,
  onCancel,
  initialData,
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
  initialData?: {
    id?: string;
    nameEnglish: string;
    nameArabic: string;
    categoryImage: string;
    categoryIcon: string;
    storeType?: 'Restaurant' | 'GroceryStore' | 'PharmacyStore';
  };
}) => {
  const { mutate } = useAddCategory();
  const { mutate: editStoreCategory } = useEditStoreCategory();
  const [loading, setLoading] = useState(false);
  const { showMessage } = useSnackbar();
  const { primary, warning, getColor } = useThemeColors();

  const initialValues = {
    englishName: initialData?.nameEnglish ?? '',
    arabicName: initialData?.nameArabic ?? '',
    image: initialData?.categoryImage ?? '',
    icon: initialData?.categoryIcon ?? '',
    storeType: initialData?.storeType ?? 'Restaurant',
  };

  const validationSchema = Yup.object().shape({
    englishName: Yup.string()
      .required('Please enter Category Name')
      .matches(
        /^[A-Za-z][A-Za-z\s\-&@'".,]*[A-Za-z]$/,
        'Category Name must start and end with letters; special characters allowed in between'
      )
      .max(50, 'Category Name cannot exceed 50 characters')
      .test(
        'not-only-spaces',
        'Category Name cannot be only spaces',
        (value) => {
          if (typeof value !== 'string') return false;
          return value.trim().length > 0;
        }
      ),
    arabicName: Yup.string()
      .required('Please enter Category Name in Arabic')
      .matches(
        /^[\u0600-\u06FF][\u0600-\u06FF\s\u061F\u060C\u061B\u0640\-_'".،؛؟&]*[\u0600-\u06FF]$/,
        'Name must start and end with Arabic letters; special characters allowed only in between'
      )
      .max(50, 'Category Name cannot exceed 50 characters')
      .test(
        'not-only-spaces',
        'Name cannot be only spaces',
        (value) => {
          if (typeof value !== 'string') return false;
          return value.trim().length > 0;
        }
      ),
    storeType: Yup.string().required('Business Type is required'),
    image: Yup.string().required('Category Image is required'),
    icon: Yup.string().required('Category Icon is required'),
  });

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting }: any
  ) => {
    try {
      setLoading(true);

      // Validate that we have image URLs
      if (!values.image || values.image.trim() === '') {
        showMessage('Category Image is required', 'error');
        setLoading(false);
        return;
      }

      if (!values.icon || values.icon.trim() === '') {
        showMessage('Category Icon is required', 'error');
        setLoading(false);
        return;
      }

      const categoryRequest: CreateRestaurantCategoryRequest = {
        createdUserType: 'Admin',
        createdUserId: '68061e75c22c7d1615af348d',
        nameEnglish: values.englishName,
        nameArabic: values.arabicName,
        categoryImage: values.image,
        categoryIcon: values.icon,
        storeCategoryType: values.storeType,
      };

      const editRequest: UpdateRestaurantCategoryRequest = {
        nameEnglish: values.englishName,
        nameArabic: values.arabicName,
        categoryImage: values.image,
        categoryIcon: values.icon,
        // storeCategoryType: values.storeType,
      };

      if (mode === 'edit' && initialData?.id) {
        editStoreCategory(
          { id: initialData.id, data: editRequest },
          {
            onSuccess: () => {
              setLoading(false);
              onCancel();
              showMessage('Category Updated Successfully', 'success');
            },
            onError: (error: any) => {
              console.error('Edit Error:', error);
              setLoading(false);
              // Error message is already handled in the mutation hook
            },
          }
        );
      } else {
        mutate(categoryRequest, {
          onSuccess: () => {
            setLoading(false);
            onCancel();
            showMessage('Category Added Successfully', 'success');
          },
          onError: () => {
            setLoading(false);
          },
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          p: 3,
          border: `1px solid ${getColor('border.default')}`,
          borderRadius: 1,
          mt: 2,
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            setFieldError,
            setFieldTouched,
          }) => {
            // Reset image fields when Business Type changes
            const prevStoreTypeRef = useRef(values.storeType);
            
            useEffect(() => {
              const handleStoreTypeChange = () => {
                // Reset all form fields when Business Type changes
                setFieldValue('englishName', '');
                setFieldValue('arabicName', '');
                setFieldValue('image', '');
                setFieldValue('icon', '');
                
                // Clear all field errors
                setFieldError('englishName', undefined);
                setFieldError('arabicName', undefined);
                setFieldError('image', undefined);
                setFieldError('icon', undefined);
                
                // Clear field touched states to remove error styling
                setFieldTouched('englishName', false);
                setFieldTouched('arabicName', false);
                setFieldTouched('image', false);
                setFieldTouched('icon', false);
              };

              if (prevStoreTypeRef.current !== values.storeType) {
                handleStoreTypeChange();
                prevStoreTypeRef.current = values.storeType;
              }
            }, [values.storeType, setFieldValue, setFieldError, setFieldTouched]);

            return (
            <Form>
              <FormSection title="Category Information">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 },
                    mt: 2,
                  }}
                >
                  <FormLabel
                    required
                    sx={{
                      width: { xs: '100%', sm: '180px' },
                      color: 'text.primary',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: { xs: 'left', sm: 'right' },
                      pr: { xs: 0, sm: 2 },
                      pt: { xs: 0, sm: 1 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                  >
                    Business Type
                  </FormLabel>
                  <FormControl
                    error={touched.storeType && Boolean(errors.storeType)}
                    required
                    sx={{ flex: 1 }}
                  >
                    {mode === 'edit' && initialData?.id ? (
                      <Typography variant="body1" sx={{ p: 1 }}>
                        {initialData?.storeType === 'Restaurant' ? 'Restaurant' :
                          initialData?.storeType === 'GroceryStore' ? 'Grocery Store' :
                            initialData?.storeType === 'PharmacyStore' ? 'Pharmacy Store' : ""}
                      </Typography>
                    ) : (
                      <RadioGroup
                        row
                        name="storeType"
                        value={values.storeType}
                        onChange={handleChange}
                        sx={{ gap: 3, mb: '4px' }}
                      >
                        <FormControlLabel
                          value="Restaurant"
                          control={<Radio />}
                          label="Restaurant"
                          sx={{
                            '& .MuiFormControlLabel-label': { fontSize: 14 },
                          }}
                        />
                        <FormControlLabel
                          value="GroceryStore"
                          control={<Radio />}
                          label="Grocery Store"
                          sx={{
                            '& .MuiFormControlLabel-label': { fontSize: 14 },
                          }}
                        />
                        <FormControlLabel
                          value="PharmacyStore"
                          control={<Radio />}
                          label="Pharmacy Store"
                          sx={{
                            '& .MuiFormControlLabel-label': { fontSize: 14 },
                          }}
                        />
                      </RadioGroup>
                    )}
                    {touched.storeType && errors.storeType && (
                      <FormHelperText>{errors.storeType}</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <FormInput
                  label="Category Name (In English)"
                  name="englishName"
                  value={values.englishName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.englishName && Boolean(errors.englishName)}
                  helperText={
                    touched.englishName && errors.englishName
                      ? String(errors.englishName)
                      : undefined
                  }
                  required
                />

                <FormInput
                  label="Category Name (In Arabic)"
                  name="arabicName"
                  value={values.arabicName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.arabicName && Boolean(errors.arabicName)}
                  helperText={
                    touched.arabicName && errors.arabicName
                      ? String(errors.arabicName)
                      : undefined
                  }
                  required
                />

                <CustomImageUploadField
                  name="image"
                  label="Category Image"
                  required
                />

                <CustomImageUploadField
                  name="icon"
                  label="Category Icon"
                  required
                />
              </FormSection>

              <Box sx={{ display: 'flex', gap: 2, mt: 3, px: '26%' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ bgcolor: primary.main }}
                >
                  {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
                </Button>
                <Button
                  variant="contained"
                  sx={{ bgcolor: warning.main }}
                  onClick={onCancel}
                  type="button"
                >
                  Cancel
                </Button>
              </Box>
            </Form>
            );
          }}
        </Formik>
      </Box>
    </Container>
  );
};

export default CategoryForm;
